import { CircularProgress } from '@mui/material'
import React, { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { dynamicClassName } from 'styles/helper'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import styles from './StatusIcon.module.less'

export type StatusIconVariants =
  | 'spinner'
  | 'queued'
  | 'generic_finished'
  | 'generic_failed'
  | 'generic_unsolvable'
  | 'regular_created'
  | 'regular_finished'
  | 'regular_failed'
  | 'regular_unsolvable'
  | 'fuelplan_created'
  | 'fuelplan_finished'
  | 'fuelplan_failed'
  | 'followup_created'
  | 'followup_finished'
  | 'followup_failed'
  | 'followup_unsolvable'
  | 'sandbox_created'
  | 'sandbox_finished'
  | 'sandbox_failed'
  | 'regular_wip_created'
  | 'regular_wip_published'
  | 'regular_wip_failed'
  | 'regular_wip_inactive'
  | 'regular_wip_warning'
  | 'intraday_created'
  | 'intraday_published'
  | 'intraday_failed'
  | 'intraday_warning'
  | 'meas_created'
  | 'meas_failed'
  | 'meas_warning'
  | 'meas_finished'
  | 'event_deactivated'
  | 'event_sent'
  | 'event_activated'
  | 'event_deleted'
  | 'settings_deviation'
  | 'settings_no_deviation'

type StatusIconProps = {
  variant: StatusIconVariants
  size?: 'small' | 'medium' | 'large'
  tooltip?: { title: ReactNode; placement?: 'top' | 'bottom' | 'left' | 'right'; arrow?: boolean }
}

// A font-glyph ring can't rotate around its own exact visual center (font metrics aren't pixel-precise),
// so it drifts off-center as it spins. An SVG-based indeterminate progress ring has no such imprecision.
const SPINNER_SIZE_PX: Record<NonNullable<StatusIconProps['size']>, number> = {
  large: 20,
  medium: 16,
  small: 12,
}

// Slower than MUI's 1.4s default, which reads as too frantic for a persistent status indicator.
const SPINNER_ANIMATION_DURATION_MS = 4000

const variantStyles: Record<StatusIconVariants, { icon: string; color: string }> = {
  //GENERAL
  spinner: { icon: 'far fa-spinner-third fa-spin', color: styles.StatusIcon__grey },
  queued: { icon: 'fas fa-clock', color: styles.StatusIcon__grey },
  // For job types with no iconography of their own — the outcome still has to read
  // as an outcome rather than as work still in progress.
  generic_finished: { icon: 'fas fa-check', color: styles.StatusIcon__green },
  generic_failed: { icon: 'fas fa-times', color: styles.StatusIcon__red },
  generic_unsolvable: { icon: 'fas fa-minus', color: styles.StatusIcon__orange },
  //REGULAR
  regular_created: { icon: 'fas fa-plus', color: styles.StatusIcon__grey },
  regular_finished: { icon: 'fas fa-check', color: styles.StatusIcon__green },
  regular_failed: { icon: 'fas fa-times', color: styles.StatusIcon__red },
  regular_unsolvable: { icon: 'fas fa-minus', color: styles.StatusIcon__orange },
  //FUELPLAN
  fuelplan_created: { icon: 'far fa-mountains', color: styles.StatusIcon__grey },
  fuelplan_finished: { icon: 'far fa-mountains', color: styles.StatusIcon__green },
  fuelplan_failed: { icon: 'far fa-mountains', color: styles.StatusIcon__red },
  //FOLLOWUP
  followup_created: { icon: 'far fa-history', color: styles.StatusIcon__grey },
  followup_finished: { icon: 'far fa-history', color: styles.StatusIcon__green },
  followup_failed: { icon: 'far fa-history', color: styles.StatusIcon__red },
  followup_unsolvable: { icon: 'far fa-history', color: styles.StatusIcon__orange },
  //SANDBOX
  sandbox_created: { icon: 'fas fa-flask', color: styles.StatusIcon__grey },
  sandbox_finished: { icon: 'fas fa-flask', color: styles.StatusIcon__green },
  sandbox_failed: { icon: 'fas fa-flask', color: styles.StatusIcon__red },
  //REGULAR WIP
  regular_wip_created: { icon: 'far fa-rocket', color: styles.StatusIcon__grey },
  regular_wip_published: { icon: 'far fa-rocket', color: styles.StatusIcon__green },
  regular_wip_failed: { icon: 'far fa-rocket', color: styles.StatusIcon__red },
  regular_wip_inactive: { icon: 'far fa-rocket', color: styles.StatusIcon__grey },
  regular_wip_warning: { icon: 'far fa-rocket', color: styles.StatusIcon__orange },
  //INTRA DAY
  intraday_created: { icon: 'far fa-bolt', color: styles.StatusIcon__grey },
  intraday_published: { icon: 'far fa-bolt', color: styles.StatusIcon__green },
  intraday_failed: { icon: 'far fa-bolt', color: styles.StatusIcon__red },
  intraday_warning: { icon: 'far fa-bolt', color: styles.StatusIcon__orange },

  //MEAS
  meas_created: { icon: 'far fa-wave-square', color: styles.StatusIcon__grey },
  meas_failed: { icon: 'far fa-wave-square', color: styles.StatusIcon__red },
  meas_finished: { icon: 'far fa-wave-square', color: styles.StatusIcon__green },
  meas_warning: { icon: 'far fa-wave-square', color: styles.StatusIcon__orange },
  //EVENTS
  event_deactivated: { icon: 'fas fa-calendar-minus', color: styles.StatusIcon__grey },
  event_sent: { icon: 'fas fa-calendar-plus', color: styles.StatusIcon__blue },
  event_activated: { icon: 'fas fa-calendar-check', color: styles.StatusIcon__green },
  event_deleted: { icon: 'fas fa-calendar-times', color: styles.StatusIcon__red },
  //SETTINGS
  settings_deviation: { icon: 'fas fa-exclamation', color: styles.StatusIcon__yellow },
  settings_no_deviation: { icon: 'fas fa-check', color: styles.StatusIcon__green },
}

export default function StatusIcon({ variant, size = 'medium', tooltip }: StatusIconProps): ReactElement {
  const { t } = useTranslation()
  const { icon, color } = variantStyles[variant]
  const isSpinner = variant === 'spinner'

  const ariaLabel = tooltip && typeof tooltip.title === 'string' ? t(tooltip.title) : undefined
  const tooltipTitle = tooltip ? (typeof tooltip.title === 'string' ? t(tooltip.title) : tooltip.title) : undefined

  const iconElement = (
    <div
      aria-label={ariaLabel}
      aria-hidden={!tooltip ? true : undefined}
      className={dynamicClassName({
        [styles.StatusIcon]: true,
        [styles.StatusIcon__large]: size === 'large',
        [styles.StatusIcon__medium]: size === 'medium',
        [styles.StatusIcon__small]: size === 'small',
        [color]: color,
      })}
    >
      {isSpinner ? (
        <CircularProgress
          size={SPINNER_SIZE_PX[size]}
          thickness={5}
          sx={{
            color: 'white',
            animationDuration: `${SPINNER_ANIMATION_DURATION_MS}ms`,
            '& .MuiCircularProgress-circle': { animationDuration: `${SPINNER_ANIMATION_DURATION_MS}ms` },
          }}
        />
      ) : (
        <i className={dynamicClassName({ [icon]: true, [styles.StatusIcon_Icon]: true })} />
      )}
    </div>
  )

  if (tooltip) {
    return (
      <Tooltip title={tooltipTitle} arrow={tooltip.arrow ?? true} placement={tooltip.placement ?? 'top'}>
        {iconElement}
      </Tooltip>
    )
  }

  return iconElement
}
